/**
* Per-replica (swarm slot) helpers for the external API dashboard (VD-725):
* the `byReplica` breakdown from `/stats` and the `replica`/`replicaLoad`
* fields on each log. Logs from before the replica tracking was deployed
* (or from outside the swarm) come with `replica: null`.
*
* The thresholds are a first guess, kept in one place so they are easy to
* tune once there are real numbers from production.
*/

export const REPLICA_THRESHOLDS = {
    // a replica getting more than this many times its fair share of requests
    unevenShareFactor: 1.5,
    // avg duration / slow % / error % this many times the other replicas' average
    outlierFactor: 2,
    // ...and at least this far above it, so 0.2% vs 0.05% errors doesn't count
    minPercentGap: 1,
    // too few requests on a replica to say anything about its averages
    minRequests: 20,
    // max event loop lag (ms)
    lagWarningMs: 100,
    lagCriticalMs: 500,
    // a single log: slow request, and "the replica was busy" when it arrived
    slowRequestMs: 1000,
    busyInFlight: 10,
};

export const isKnownReplica = (replica) => replica !== null && replica !== undefined && replica !== '';

export const replicaLabel = (replica, lang) => (
    isKnownReplica(replica) ? `${'Replica'.translate(lang)} ${replica}` : 'Unknown (old logs)'.translate(lang)
);

const num = (v) => (v === null || v === undefined || isNaN(Number(v)) ? null : Number(v));

const average = (values) => {
    let list = values.filter((v) => v !== null);
    if (!list.length) return null;
    return list.reduce((sum, v) => sum + v, 0) / list.length;
};

const SEVERITY_RANK = { warning: 1, critical: 2 };

/**
* Sorts the rows by slot (the `null` row last), works out each replica's
* share among the replicas that are known and flags the ones that stand out
* against the average of the *other* replicas.
*
* @param {object} [options]
* @param {string} [options.durationKey='avgDurationMs'] - the speed field to compare (the all-requests page compares `p95Ms`)
* @returns {{rows: object[], replicaCount: number, idealPercent: number|null}}
*   each row is the backend row plus `sharePercentOfKnown`, `issues`
*   ([{severity, reason}]) and `severity` ('warning'|'critical'|null)
*/
export const analyzeReplicas = (byReplica, { durationKey = 'avgDurationMs' } = {}) => {
    let known = (byReplica || []).filter((r) => isKnownReplica(r.replica)).sort((a, b) => Number(a.replica) - Number(b.replica));
    let unknown = (byReplica || []).filter((r) => !isKnownReplica(r.replica));
    let knownRequests = known.reduce((sum, r) => sum + (Number(r.requests) || 0), 0);
    let replicaCount = known.length;
    let idealPercent = replicaCount ? 100 / replicaCount : null;
    let t = REPLICA_THRESHOLDS;

    let rows = known.map((row) => {
        // requestsPercent counts the old (replica: null) logs too, so the
        // fair-share check uses the share among known replicas instead
        let sharePercentOfKnown = knownRequests ? ((Number(row.requests) || 0) / knownRequests) * 100 : 0;
        let issues = [];

        if (replicaCount >= 2) {
            if (sharePercentOfKnown > idealPercent * t.unevenShareFactor) {
                issues.push({ severity: 'warning', reason: 'Uneven distribution' });
            }

            let others = known.filter((r) => r !== row);
            let enoughData = (Number(row.requests) || 0) >= t.minRequests;

            if (enoughData) {
                let avgDuration = average(others.map((r) => num(r[durationKey])));
                let slow = average(others.map((r) => num(r.slowPercent)));
                let errors = average(others.map((r) => num(r.errorPercent)));
                let value;

                value = num(row[durationKey]);
                let slower = avgDuration && value !== null && value > avgDuration * t.outlierFactor;

                value = num(row.slowPercent);
                let moreSlow = slow !== null && value !== null && value > slow * t.outlierFactor && value - slow >= t.minPercentGap;

                if (slower || moreSlow) {
                    issues.push({ severity: 'critical', reason: 'Slower than the other replicas' });
                }

                value = num(row.errorPercent);
                if (errors !== null && value !== null && value > errors * t.outlierFactor && value - errors >= t.minPercentGap) {
                    issues.push({ severity: 'critical', reason: 'More errors than the other replicas' });
                }
            }
        }

        let lag = num(row.maxEventLoopLagMs);
        if (lag !== null && lag > t.lagCriticalMs) {
            issues.push({ severity: 'critical', reason: 'Replica congested' });
        } else if (lag !== null && lag > t.lagWarningMs) {
            issues.push({ severity: 'warning', reason: 'Replica congested' });
        }

        let severity = issues.reduce((worst, i) => (!worst || SEVERITY_RANK[i.severity] > SEVERITY_RANK[worst] ? i.severity : worst), null);

        return { ...row, sharePercentOfKnown, issues, severity };
    });

    unknown.forEach((row) => rows.push({ ...row, sharePercentOfKnown: null, issues: [], severity: null }));

    return { rows, replicaCount, idealPercent };
};

/**
* For a slow log: was the replica already busy when the request arrived
* (event loop lagging, or many other requests in flight), or is the request
* just slow on its own? Returns null when the request isn't slow or there is
* no replica data for it.
*
* @returns {{congested: boolean, text: string}|null}
*/
export const replicaLoadHint = (log, lang) => {
    let t = REPLICA_THRESHOLDS;
    if (!log || !log.replicaLoad || !(Number(log.durationMs) >= t.slowRequestMs)) return null;

    let lag = num(log.replicaLoad.eventLoopLagMs);
    let inFlight = num(log.replicaLoad.inFlight);
    let congested = (lag !== null && lag > t.lagWarningMs) || (inFlight !== null && inFlight >= t.busyInFlight);

    return {
        congested,
        text: (congested ? 'The replica was congested when the request arrived' : 'The request is slow on its own').translate(lang),
    };
};
